import { defineComponent, h, resolveComponent } from 'vue'

import { bsComponentProps, useBsComponent } from '../useBsComponent'
import { useVm } from '../../../composables/useVm'
import { domHandlers } from '../quasarHandlers'

/**
 * Anything clickable that is not a button: a card, a row, a tile. Renders as a `router-link` when it has
 * a `to`, and as a div with a button role otherwise, so both are reachable by keyboard.
 *
 * Presentational — it takes the bundle's listeners and classes and nothing else.
 */
export default defineComponent({
  name: 'BsClickable',

  /**
   * Declared so `input` is an emitted event rather than a fallthrough attribute: undeclared, the
   * `onInput` this render attaches stays in `$attrs` and Vue applies it to the root element as well,
   * so a single DOM input event ran the handler twice.
   */
  emits: ['input'],

  props: {
    ...bsComponentProps,
    to: { type: String, default: void 0 },
    preventNavigation: { type: Boolean, default: false },
    /**
     * Declared, so it does not fall through as an attribute — and rendered below only when it is true.
     *
     * Vue 2 removed any attribute whose value was `false`. Vue 3 keeps it, as the string "false", for
     * every name outside its short list of seven special booleans (`disabled` is not on it) and for
     * every element that has no matching DOM property — which a `div` does not. So `:disabled="false"`
     * put a literal `disabled="false"` on the root, `app.sass`'s `[disabled] { pointer-events: none }`
     * matched it, and the arc wizard's ebook/audiobook tiles could not be clicked at all: eight of the
     * CRUD spec's tests, and three whole suites through their `before all`.
     */
    disabled: { type: Boolean, default: false }
  },

  setup (props, { emit }) {
    return { ...useBsComponent({ vm: useVm(), props, emit }) }
  },

  render () {
    const data = {
      class: ['bs-clickable', this.getClasses({
        'bs-clickable__navigation-prevented': this.preventNavigation
      })],
      ...domHandlers(this.getListeners()),
      to: this.to || void 0,
      role: this.to ? 'link' : 'button',
      tabindex: 0,
      // `void 0` rather than `false`, so the attribute is absent when it is not disabled. See the prop.
      disabled: this.disabled || void 0
    }

    /**
     * `resolveComponent`, not the string tag. A string in `h()` is an ELEMENT in Vue 3 -- only compiled
     * templates resolve one through the component registry -- so `h('router-link')` rendered a literal
     * <router-link> element and never navigated.
     *
     * The component gets its children as a slot function and the div as an array. Handed the array,
     * router-link still rendered it, but Vue warned "Non-function value encountered for default slot"
     * for every clickable card in dev.
     */
    return this.to
      ? h(resolveComponent('router-link'), data, { default: () => this.slot(this, 'default') })
      : h('div', data, this.slot(this, 'default'))
  }
})
